import FormFieldCalendar from '@/components/FormFieldCalendar';
import FormFieldComponent from '@/components/FormFieldComponent';
import ToolBar from '@/components/ToolBar';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { CircleCheckBig, Flag, SquareCheckBig, SquarePen } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  useDDIWorkOrderDetail,
  useDdiInvoiceByNumber,
  usePatchDdiInvoiceMutation,
} from '@/services/dueDiligenceService';
import { formatCurrency, genUniqueId } from '@/lib/utils';
import { DdiInvoiceFormSchema, type DdiInvoiceFormSchemaType } from './ddiInvoice.schema';
import type { DdiInvoicePatchRequest, DdiInvoiceStatus } from '@/types/dueDiligence.types';
import AlertBox from '@/components/AlertBox';
import Loader from '@/components/Loader';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUserStore } from '@/stores/user';
import { isSupervisor } from '@/services/userService';
import { hasFeature } from '@/services/featureService';
import ApplyPaymentModal from '@/components/ApplyPaymentModal';
import { InvoiceStatusBadge, invoiceStatus } from '@pages/caseManagement/invoice/InvoiceView';
import { DdiInvoiceBillingInformationTable, type DdiInvoiceBillingRow } from './DdiInvoiceBillingInformationTable';

const buildBillingPatch = (
  billingItems: DdiInvoiceBillingRow[],
  originalItems: { billingItemId: string; code: string; description: string; amount: number }[]
): Pick<DdiInvoicePatchRequest, 'addBillingItems' | 'updateBillingItems' | 'removeBillingItemIds'> => {
  const originalBillingItemIds = originalItems.map((item) => item.billingItemId);
  const currentBillingItemIds = billingItems
    .filter((item): item is DdiInvoiceBillingRow & { billingItemId: string } => Boolean(item.billingItemId))
    .map((item) => item.billingItemId);

  const addBillingItems = billingItems
    .filter((item) => !item.billingItemId)
    .map((item) => ({
      code: item.itemId,
      description: item.description,
      amount: Number(item.amount),
    }));

  const updateBillingItems: NonNullable<DdiInvoicePatchRequest['updateBillingItems']> = [];
  billingItems.forEach((item) => {
    if (!item.billingItemId) return;
    const originalItem = originalItems.find((orig) => orig.billingItemId === item.billingItemId);
    if (
      originalItem &&
      (Math.abs(Number(originalItem.amount) - Number(item.amount)) > 0 ||
        originalItem.description !== item.description ||
        originalItem.code !== item.itemId)
    ) {
      updateBillingItems.push({
        billingItemId: item.billingItemId,
        code: item.itemId,
        description: item.description,
        amount: Number(item.amount),
      });
    }
  });

  const removeBillingItemIds = originalBillingItemIds.filter(
    (originalId) => originalId && !currentBillingItemIds.includes(originalId)
  );

  return {
    ...(addBillingItems.length > 0 ? { addBillingItems } : {}),
    ...(updateBillingItems.length > 0 ? { updateBillingItems } : {}),
    ...(removeBillingItemIds.length > 0 ? { removeBillingItemIds } : {}),
  };
};

export const DdiInvoiceView = ({ mode }: { mode: 'view' | 'edit' }) => {
  const navigate = useNavigate();
  const { invoiceNumber: id } = useParams();
  const [searchParam] = useSearchParams();

  const form = useForm<DdiInvoiceFormSchemaType>({
    mode: 'onBlur',
    resolver: zodResolver(DdiInvoiceFormSchema),
  });

  const patchInvoice = usePatchDdiInvoiceMutation();
  const { control, reset, setValue } = form;
  const { isValid } = form.formState;
  const status = useWatch({ control, name: 'status' });

  const userStore = useUserStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const disabledStyleClass = '!border-[#CAC8C8] !bg-[#F5F5F5] !opacity-100';
  const [isAlertBoxOpen, setIsAlertBoxOpen] = useState(false);
  const hasApplyPaymentFeature = hasFeature('accounting.applyPayment');
  const [openApplyPaymentModal, setOpenApplyPaymentModal] = useState(false);
  const [billingItems, setBillingItems] = useState<DdiInvoiceBillingRow[]>([]);

  const invoiceQuery = useDdiInvoiceByNumber(id, refreshKey);
  const data = invoiceQuery.data;
  const workOrderId = data?.workOrderId ?? searchParam.get('workOrderId') ?? null;
  const workOrderQuery = useDDIWorkOrderDetail(workOrderId);

  const workOrder = workOrderQuery.data;
  const isLoading = invoiceQuery.isLoading || (!!workOrderId && workOrderQuery.isLoading);
  const isError =
    invoiceQuery.isError ||
    (!invoiceQuery.isLoading && !data) ||
    (!!workOrderId && workOrderQuery.isError) ||
    (!!data && !!id && data.invoiceNumber !== id);

  const hasBillingChanges = useMemo(() => {
    if (!data) return false;
    const patch = buildBillingPatch(billingItems, data.billingItems);
    return (
      (patch.addBillingItems?.length ?? 0) > 0 ||
      (patch.updateBillingItems?.length ?? 0) > 0 ||
      (patch.removeBillingItemIds?.length ?? 0) > 0
    );
  }, [billingItems, data]);

  useEffect(() => {
    if (!data || !workOrder) return;

    reset({
      invoiceId: data.invoiceNumber ?? '',
      clientName: workOrder.clientDetails.clientName ?? '',
      toServe: workOrder.searchDetails.personForInquiry ?? workOrder.searchDetails.entityForInquiry ?? '',
      attorney: workOrder.clientDetails.lawyerName ?? '',
      contact: workOrder.clientDetails.contactName ?? '',
      status: data.status ?? 'DRAFT',
      billingDate: data.billDate ? new Date(data.billDate) : undefined,
      caseName: workOrder.clientDetails.caseName ?? '',
    });

    setBillingItems(
      data.billingItems.map((item) => ({
        id: genUniqueId(),
        itemId: item.code,
        description: item.description,
        amount: Number(item.amount) || 0,
        billingItemId: item.billingItemId,
      }))
    );
  }, [data, workOrder, refreshKey, reset]);

  useEffect(() => {
    const totalAmount =
      billingItems.reduce((acc, item) => {
        const itemAmount = item.amount || 0;
        return acc + itemAmount;
      }, 0) ?? 0;
    setValue('amount', billingItems.length ? formatCurrency(totalAmount ?? 0) : formatCurrency(0));
  }, [billingItems, setValue]);

  useEffect(() => {
    if (mode === 'edit' && !isLoading && (data?.status === 'CONFIRMED' || data?.status === 'PAID') && id) {
      navigate(`/due-diligence/invoice/${id}${workOrderId ? `?workOrderId=${workOrderId}` : ''}`);
    }
  }, [mode, isLoading, data?.status, id, workOrderId, navigate]);

  const saveHandler = (statusUpdate?: DdiInvoiceStatus) => {
    if (!workOrderId || !data) return;

    const updatePayload: DdiInvoicePatchRequest = {};

    if (statusUpdate === 'CONFIRMED' || statusUpdate === 'PAID') {
      updatePayload.status = statusUpdate;
    } else {
      Object.assign(updatePayload, buildBillingPatch(billingItems, data.billingItems));
    }

    if (
      !updatePayload.status &&
      !updatePayload.addBillingItems &&
      !updatePayload.updateBillingItems &&
      !updatePayload.removeBillingItemIds
    ) {
      return;
    }

    patchInvoice.mutate(
      { workOrderId, body: updatePayload },
      {
        onSuccess: () => {
          if (mode === 'edit') {
            navigate(`/due-diligence/invoice/${id}${workOrderId ? `?workOrderId=${workOrderId}` : ''}`);
          }
          toast({
            description: 'Invoice updated successfully',
          });
          setIsAlertBoxOpen(false);
          setRefreshKey((prev) => prev + 1);
          void invoiceQuery.refetch();
        },
        onError: (error) => {
          displayError(error || 'Failed to update invoice');
        },
      }
    );
  };

  return (
    <NotFoundRedirectionWrapper itemName="Invoice" isItemNotFound={isError}>
      <Loader className="w-full scrollbar-hide flex gap-6 flex-col" isLoading={isLoading}>
        <div className="w-full flex flex-row gap-2">
          {workOrder?.summary.isRush && (
            <div className="text-error h-5 w-5 pt-2" title="Rush Case">
              <Flag className="text-[#D72222] w-4 h-4" fill="#D72222" />
            </div>
          )}
          <ToolBar
            title={`W/O: ${workOrder?.commonDetails.workOrderNumber} - Invoice`}
            secondaryTitle={<InvoiceStatusBadge key={status} status={invoiceStatus[status ?? 'DRAFT']} />}
            additionalButton={
              <div className="flex flex-row gap-6 items-center">
                {mode === 'view' && (
                  <>
                    {data?.status !== 'CONFIRMED' && data?.status !== 'PAID' && (
                      <div
                        key="edit"
                        className="border-[1px] border-[#CAC8C8] p-1 h-[32px] w-[32px] rounded-md cursor-pointer"
                        onClick={() => navigate(`update${workOrderId ? `?workOrderId=${workOrderId}` : ''}`)}
                      >
                        <SquarePen className="text-placeholder" />
                      </div>
                    )}

                    {['DRAFT', 'REVIEWED'].includes(status ?? '') &&
                      (['ADMIN', 'OPERATOR'].includes(userStore.user?.roleId ?? '') ||
                        isSupervisor(userStore.user)) && (
                        <Loader isLoading={patchInvoice.isPending}>
                          <Button
                            variant="outline"
                            className="h-[36px]"
                            onClick={() => {
                              saveHandler('CONFIRMED');
                            }}
                          >
                            <div className="flex flex-row gap-3 items-center">
                              <SquareCheckBig className="h-4 w-4" />
                              Mark as Confirmed
                            </div>
                          </Button>
                        </Loader>
                      )}
                    {hasApplyPaymentFeature &&
                      status === 'CONFIRMED' &&
                      ['ADMIN', 'ACCOUNTANT'].includes(userStore.user?.roleId ?? '') && (
                        <Button
                          className="h-[36px] bg-[#1B8652] hover:bg-[#1B8652]"
                          onClick={() => {
                            setOpenApplyPaymentModal(true);
                          }}
                          key={refreshKey}
                        >
                          <div className="flex flex-row gap-3 items-center">
                            <CircleCheckBig className="h-4 w-4" />
                            Apply Payment
                          </div>
                        </Button>
                      )}
                  </>
                )}
              </div>
            }
          />
        </div>
        <FormProvider {...form}>
          <div className="grid grid-cols-12 gap-6 w-full">
            <div className="col-span-12 md:col-span-6 space-y-4">
              <FormFieldComponent
                name="invoiceId"
                control={control}
                disabled
                label="Invoice #"
                defaultValue={data?.invoiceNumber ?? ''}
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="clientName"
                control={control}
                disabled
                defaultValue={workOrder?.clientDetails.clientName ?? ''}
                label="Client Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="caseName"
                control={control}
                disabled
                defaultValue={workOrder?.clientDetails.caseName ?? ''}
                label="Case Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="toServe"
                control={control}
                disabled
                label="Person Served"
                defaultValue=""
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="attorney"
                control={control}
                disabled
                label="Attorney Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="contact"
                control={control}
                disabled
                label="Contact Name"
                disabledStyle={disabledStyleClass}
              />
            </div>

            <div className="col-span-12 md:col-span-6 border-[1px] border-[#CAC8C8] h-full rounded-md">
              <div className="bg-[#f5f5f5] rounded-md p-6 font-bold">Billing Information</div>
              <div>
                <DdiInvoiceBillingInformationTable
                  mode={mode}
                  billingItems={billingItems}
                  setBillingItems={setBillingItems}
                  key={`invoice-billing-information-table-${billingItems.length}`}
                />
              </div>
            </div>
            <div className="col-span-6 md:col-span-6 space-y-4">
              <FormFieldCalendar
                name="billingDate"
                control={control}
                label="Billing Date"
                disabled
                disabledStyle={disabledStyleClass}
                placeholder="Invoice not confirmed"
              />
            </div>
            <div className="col-span-6 md:col-span-6 space-y-4">
              <FormFieldComponent
                name="amount"
                control={control}
                disabled
                label="Total Billing Amount"
                disabledStyle={disabledStyleClass}
              />
            </div>
          </div>
        </FormProvider>

        {openApplyPaymentModal && (
          <ApplyPaymentModal
            open={openApplyPaymentModal}
            onOpenChange={setOpenApplyPaymentModal}
            invoices={
              data?.invoiceNumber
                ? [
                    {
                      invoiceNumber: data.invoiceNumber,
                      invoiceAmount: data.totalAmount ?? 0,
                      balance: data.balance ?? 0,
                    },
                  ]
                : []
            }
            onSuccess={(paymentData) => {
              setOpenApplyPaymentModal(false);
              const remainingBalance = paymentData.invoices[0]?.balance ?? 0;
              if (remainingBalance <= 0) {
                saveHandler('PAID');
              } else {
                setRefreshKey((prev) => prev + 1);
                void invoiceQuery.refetch();
              }
            }}
          />
        )}

        {mode === 'edit' && (
          <div className="flex flex-row gap-3 w-full justify-end pb-6 pt-10">
            <Button
              variant="outline"
              className="h-[36px] w-[112px]"
              onClick={() =>
                navigate(`/due-diligence/invoice/${id}${workOrderId ? `?workOrderId=${workOrderId}` : ''}`)
              }
            >
              Cancel
            </Button>
            <AlertBox
              triggerButton={
                <Button
                  variant="default"
                  className="h-[36px] w-[103px] hover:bg-[#12375C]"
                  disabled={!isValid || !hasBillingChanges}
                  onClick={() => setIsAlertBoxOpen(true)}
                  data-testid="trigger-save-button"
                >
                  Save
                </Button>
              }
              title={`Are you sure?`}
              description={`Would you like to update invoice details?`}
              cancelBtnLabel="No"
              confirmBtnLabel="Yes"
              open={isAlertBoxOpen}
              isLoading={patchInvoice.isPending}
              cancelBtnClick={() => setIsAlertBoxOpen(false)}
              confirmBtnClick={() => saveHandler()}
            />
          </div>
        )}
      </Loader>
    </NotFoundRedirectionWrapper>
  );
};

export default DdiInvoiceView;
